import { webApi } from '@mi-front/api-clients';
import { DEFAULT_QUALITY_MINIMAP_CONFIG, QUALITY_TIMELINE } from '../consts';
import { qualityReportColor } from '../helpers/quality-report-color';
import { useQualityStatusColors } from '../useQualityStatusColors';
import { VisibleTimeRange } from '../lib/visible-time-range';
import { MinimapConfig } from './minimap-config';
import { useMinimapBins } from './useMinimapBins';

export type BrushMinimapProps = {
  report: webApi.StreamQualityReportResponseDto;
  fullDomain: VisibleTimeRange;
  innerWidth: number;
  height: number;
  minimapConfig?: MinimapConfig;
  /** Subset of stations for bin aggregation; omit to use all stations in the report. */
  stationIds?: string[];
};

/**
 * Bar heatmap behind the stream availability brush: one column per time bin, colored by share of
 * stations without issues/gaps in that interval. Fill uses the same default quality gradient as
 * the rest of the report (offline → poor-quality → online).
 */
export function BrushMinimap({
  report,
  fullDomain,
  innerWidth,
  height,
  minimapConfig,
  stationIds,
}: BrushMinimapProps) {
  const { minimapGradientStops } = useQualityStatusColors();
  const mergedMinimapConfig: MinimapConfig = {
    ...DEFAULT_QUALITY_MINIMAP_CONFIG,
    ...minimapConfig,
  };

  const bins = useMinimapBins({
    report,
    fullDomain,
    innerWidth,
    minimapConfig: mergedMinimapConfig,
    stationIds,
  });

  const opacityRange =
    mergedMinimapConfig.fillOpacityRange ?? DEFAULT_QUALITY_MINIMAP_CONFIG.fillOpacityRange!;

  if (bins.length === 0) {
    return (
      <rect
        width={innerWidth}
        height={height}
        fill={QUALITY_TIMELINE.chartColors.minimapNeutral}
        fillOpacity={0.5}
        rx={1}
      />
    );
  }

  return (
    <g>
      {bins.map((bin, index) => {
        const fill = qualityReportColor(bin.successRate, minimapGradientStops);
        const fillOpacity =
          opacityRange.min + bin.successRate * (opacityRange.max - opacityRange.min);
        return (
          <rect
            key={index}
            x={bin.x}
            y={0}
            width={bin.width}
            height={height}
            fill={fill}
            fillOpacity={fillOpacity}
          />
        );
      })}
    </g>
  );
}
